// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useCallback, useEffect, useState } from 'react';
import styles from './Field.module.scss';
import { mergeClasses } from 'util/commons';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import {
  validateEmailAddress,
  validatePhoneNumber,
  validateSpecialCharactersWithJapanese,
} from 'util/validator';
import Popover from 'components/atoms/Popover';
import { useTranslation } from 'react-i18next';

interface FieldProps {
  label: string;
  value?: string | number | null;
  format?: 'freeText' | 'email' | 'phone' | 'notSpecialCharacter';
  required?: boolean;
  maxLength?: number;
  regex?: RegExp;
  onEdit?: (value: string) => Promise<void>;
}

export const Field = ({
  label,
  value,
  format = 'freeText',
  required = false,
  maxLength,
  regex,
  onEdit,
}: FieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(String(value ?? ''));
  const [error, setError] = useState<string | undefined>(undefined);
  const { i18n } = useTranslation();

  const validateByFormat = useCallback(
    (currentValue: string) => {
      if (!required && currentValue.length === 0) {
        return undefined;
      }

      if (format === 'email') {
        return validateEmailAddress(currentValue, {});
      }

      if (format === 'phone') {
        return validatePhoneNumber(label, 15, currentValue);
      }

      if (format === 'notSpecialCharacter') {
        return validateSpecialCharactersWithJapanese(
          label,
          maxLength ?? 100,
          currentValue,
          required
        );
      }

      return undefined;
    },
    [format, label, required, maxLength, i18n.language]
  );

  const handleCancel = () => {
    setInputValue(String(value ?? ''));
    setError(undefined);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setInputValue(String(value ?? ''));
    setError(undefined);
    setIsEditing(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = event.target.value;

    if (
      (maxLength && currentValue.length > maxLength) ||
      (regex && currentValue && !regex.test(currentValue))
    ) {
      return;
    }

    setInputValue(currentValue);
  };

  const handleSubmit = () => {
    if (error) return;

    onEdit?.(inputValue.trim()).then(() => {
      setIsEditing(false);
    });
  };

  useEffect(() => {
    if (
      format === 'email' ||
      format === 'phone' ||
      format === 'notSpecialCharacter'
    ) {
      setError(validateByFormat(inputValue));
    }
  }, [inputValue, format, validateByFormat, i18n.language]);

  return (
    <div className={mergeClasses(styles.field)}>
      <p className={styles.label}>{label}</p>
      <div className={styles.valueWrapper}>
        <div className={styles.value}>
          {isEditing ? (
            <input
              type="text"
              className={mergeClasses(styles.input, error && styles.inputError)}
              autoFocus
              value={inputValue}
              onChange={handleChange}
              onBlur={handleCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              maxLength={maxLength}
            />
          ) : (
            <Popover
              content={value ?? '-'}
              positions={['top']}
              autoGen={{
                lines: 2,
                odd: 4,
              }}
            >
              <p title={value?.toString() ?? ''}>
                {value != null && value.toString() !== '' ? value : '-'}
              </p>
            </Popover>
          )}

          {onEdit && (
            <div
              className={mergeClasses(styles.editIcon, {
                [styles.disabled]: isEditing && !!error,
              })}
              onMouseDown={(e) => e.preventDefault()}
            >
              {isEditing ? (
                <IconCheck
                  size={32}
                  onClick={handleSubmit}
                  disabled={!!error}
                />
              ) : (
                <IconEdit onClick={handleEditClick} />
              )}
            </div>
          )}
        </div>
        {isEditing && error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  );
};
